import React, { forwardRef, useId } from "react";
import { cn } from "../../lib/utils.js";
import { TONES } from "../../lib/tones.js";

/* Spinner — a loading indicator atom (C7, wave 2 design-system programme).
   `variant="ring"` (default) is Button's private spinner, promoted to a
   shared part; `variant="dots"` is the Player AI typing indicator (the rail's
   `Dot` helper), folded in from chat-typing-indicator. Neither call site is
   edited by this ticket — see packages/ui/CLAUDE.md's Design-system
   programme notes.

   Every element is a <span>, so a Spinner is phrasing content: valid inside
   p, label and button elements. With no label the indicator IS the root, so
   `<Spinner aria-hidden size="sm" />` is Button's loading span plus
   motion-reduce:animate-none, with no wrapper element. */

export type SpinnerVariant = "ring" | "dots";
export type SpinnerSize = "xs" | "sm" | "md" | "lg";
/** A deliberate subset of StatusTone: a spinner is never "healthy"/"watch"/
 *  "critical" — those describe a settled state, not a wait. */
export type SpinnerTone = "neutral" | "info" | "ai";

const RING_SIZE: Record<SpinnerSize, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-6 w-6",
};

const DOT_SIZE: Record<SpinnerSize, string> = {
  xs: "w-1 h-1",
  sm: "w-1 h-1",
  md: "w-1.5 h-1.5",
  lg: "w-1.5 h-1.5",
};

const DOT_GAP: Record<SpinnerSize, string> = {
  xs: "gap-0.5",
  sm: "gap-0.5",
  md: "gap-1",
  lg: "gap-1",
};

const DOT_DELAYS = ["0ms", "120ms", "240ms"] as const;

/** `className` lands on the root: the ring or the dots row when there is no
 *  label, the inline-flex row around indicator and label when there is. */
export interface SpinnerProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "aria-hidden" | "children"
> {
  variant?: SpinnerVariant;
  size?: SpinnerSize;
  /** Absent = currentColor (ring border-current, dots bg-current); a tone
   *  reads the ring from lib/tones.ts' `text` entry (which `border-current`
   *  picks up) and the dots from its `dot` entry. */
  tone?: SpinnerTone;
  /** Visible text after the indicator; inherits the host's text size and
   *  colour — tone paints only the indicator. */
  label?: React.ReactNode;
  /** Renders with no `role`/name, for a Spinner nested inside another live
   *  region (e.g. Button's loading branch, AsyncSection) so it never
   *  double-announces. */
  "aria-hidden"?: boolean;
}

export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(
  function Spinner(
    {
      variant = "ring",
      size = "sm",
      tone,
      label,
      "aria-hidden": ariaHidden,
      className,
      ...rest
    },
    ref,
  ) {
    const labelId = useId();
    const root = {
      ref,
      role: ariaHidden ? undefined : "status",
      "aria-hidden": ariaHidden ? true : undefined,
      "aria-label": !ariaHidden && !label ? "Loading" : undefined,
      "aria-labelledby": !ariaHidden && label ? labelId : undefined,
      ...rest,
    };
    // Without a label the indicator carries the root props itself.
    const own = label ? undefined : root;
    const ownClass = label ? undefined : className;

    const indicator =
      variant === "dots" ? (
        <span {...own} className={cn("inline-flex", DOT_GAP[size], ownClass)}>
          {DOT_DELAYS.map((delay) => (
            <span
              key={delay}
              style={{ animationDelay: delay }}
              className={cn(
                DOT_SIZE[size],
                "rounded-full animate-pulse motion-reduce:animate-none",
                tone ? TONES[tone].dot : "bg-current",
              )}
            />
          ))}
        </span>
      ) : (
        <span
          {...own}
          className={cn(
            "inline-block",
            RING_SIZE[size],
            "animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none",
            tone && TONES[tone].text,
            ownClass,
          )}
        />
      );

    if (!label) return indicator;
    return (
      <span
        {...root}
        className={cn("inline-flex items-center gap-2", className)}
      >
        {indicator}
        <span id={labelId}>{label}</span>
      </span>
    );
  },
);
